import React, {
    ComponentProps,
    FC,
    FocusEventHandler,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import cn from 'classnames';
import { Button, Icon, Transition, Typography } from '@sravni/react-design-system';
import { Edit } from '@sravni/react-icons';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AnyObjectSchema } from 'yup';
import { useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import { getValidFields } from '../../../../../helpers/form.new';
import { extendFormData } from '../../../../../store/reducers/formStore';
import { StepContext } from '../../../creator/step.context';
import { useFormData } from '../../../../../store/selectors/formStore';
import { StepConfig } from '../../types';
import { isSatisfyOption, scrollToElementById } from '../../../../../helpers';
import { FORM_FIELDS } from '../../../../../constants';
import { FocusedSheet } from './components/focused.sheet';
import { StickySubmit } from './components/sticky.submit';
import { useStepSubtitle } from './helpers';
import styles from './styles.module.scss';

type ItemProps = ComponentProps<'div'> & {
    step: StepConfig;
    stepIndex: number;
    currentStep: number;
    onEditClick: (step: number) => void;
};

const { Text } = Typography;
const { Collapse } = Transition;

const FormStep: FC<ItemProps> = (props) => {
    const { step, stepIndex, currentStep, className, onEditClick } = props;
    const form = useFormContext();
    const { stepsConfig } = useContext(StepContext);
    const subTitle = useStepSubtitle(stepIndex);

    const stepId = useMemo(() => `${stepsConfig[stepIndex].stepName}.id`, []);

    const {
        clearErrors,
        formState: { dirtyFields: _ },
    } = form;

    const onClick = useCallback(() => {
        onEditClick(stepIndex);
    }, [onEditClick, stepIndex]);

    const { showStepContent, showEditBtn, lockedStep } = useMemo(
        () => ({
            showStepContent: stepIndex === currentStep,
            showEditBtn: stepIndex < currentStep,
            lockedStep: stepIndex > currentStep,
        }),
        [stepIndex, currentStep],
    );

    useEffect(() => {
        if (!showStepContent) {
            clearErrors();
        } else {
            setTimeout(() => scrollToElementById(stepId), 200);
        }
    }, [currentStep]);

    return (
        <div className={cn(styles['form-accordion__item'], className)} id={stepId}>
            <div className={styles['step-header']}>
                <div
                    className={cn(styles['step-header__content'], {
                        [styles['step-header__content_color-gray']]: lockedStep,
                    })}>
                    <Icon background="gray" size={20}>
                        {step.icon}
                    </Icon>
                    <div className={styles['step-header__text']}>
                        <Text size={16} className={styles['step-title']}>
                            {step.title}
                        </Text>
                        {!showStepContent && (
                            <Text size={12} className={styles['step-subtitle']}>
                                {subTitle}
                            </Text>
                        )}
                    </div>
                </div>
                {showEditBtn && (
                    <Button variant="text" className={styles['step-header__edit-btn']} onClick={onClick}>
                        <Icon color="blue" background="white" size={20}>
                            <Edit />
                        </Icon>
                    </Button>
                )}
            </div>
            <Collapse
                visible={showStepContent}
                unmountOnExit
                className={cn({ [styles['step-collapse']]: showStepContent })}>
                <div className={styles['step-content']}>
                    <step.View />
                </div>
            </Collapse>
        </div>
    );
};

export const FormStepWrapper: FC<ItemProps> = (props) => {
    const { step, stepIndex, currentStep } = props;
    const dispatch = useDispatch();
    const { nextStep } = useContext(StepContext);
    const formData = useFormData();
    const stickyContainerRef = useRef<HTMLElement | null>();

    const formRef = useRef<HTMLFormElement | null>(null);

    const form = useForm({
        resolver: yupResolver(step.schema() as AnyObjectSchema),
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const {
        reset,
        trigger,
        handleSubmit,
        clearErrors,
        formState: { dirtyFields: _, errors },
    } = form;

    useEffect(() => {
        stickyContainerRef.current = document.getElementById('stickySubmitContainer');

        const formElement = formRef.current;

        if (!formElement) {
            return;
        }

        formElement.addEventListener('manualBlur' as any, formOnBlur);

        return () => {
            formElement.removeEventListener('manualBlur' as any, formOnBlur);
        };
    }, []);

    useEffect(() => {
        reset({ ...formData }, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    const stringifiedErrors = Object.keys(errors).join(' ');

    const validateAndSaveFields = useCallback(
        async (fields: Array<string> | string) => {
            const isValid = await trigger(fields);
            if (isValid) {
                await trigger(Object.keys(errors));
            }
            const validFields = getValidFields(form);
            dispatch(extendFormData(validFields));
        },
        [form, trigger, dispatch, stringifiedErrors],
    );

    const formOnBlur: React.FocusEventHandler<HTMLFormElement> = useCallback(
        async (event) => {
            if (event.type === 'manualBlur') {
                const { fields = [] } = (event as unknown as CustomEvent).detail;
                return await validateAndSaveFields(fields);
            }

            const { name: fieldName } = event.target;

            if (event.type !== 'blur' || !fieldName || !isSatisfyOption(Object.values(FORM_FIELDS), fieldName)) {
                return;
            }

            await validateAndSaveFields(fieldName);
        },
        [validateAndSaveFields],
    );

    const formOnFocus: FocusEventHandler<HTMLFormElement> = (event) => {
        const { name: fieldName } = event.target;
        if (event.type !== 'focus' || !fieldName || !isSatisfyOption(Object.values(FORM_FIELDS), fieldName)) {
            return;
        }
        clearErrors(fieldName);
    };

    const goNext = useCallback(async () => {
        nextStep();
    }, [nextStep]);

    const isActiveStep = stepIndex === currentStep;

    return (
        <FormProvider {...form}>
            <form
                className={styles['step-form']}
                onBlur={formOnBlur}
                onSubmit={handleSubmit(goNext)}
                onFocus={formOnFocus}
                ref={formRef}>
                <FormStep {...props} />
                {isActiveStep && (
                    <>
                        <FocusedSheet />
                        {stickyContainerRef.current &&
                            createPortal(<StickySubmit nextBtnText={step.submitBtnText} />, stickyContainerRef.current)}
                    </>
                )}
            </form>
        </FormProvider>
    );
};
